export interface Point2D {
  x: number;
  y: number;
}

export class CollisionUtils {
  /**
   * Vérifie si deux polygones (convexes) entrent en collision en utilisant le théorème de l'axe séparateur (SAT).
   * @param polyA Tableau de points {x, y} représentant les sommets du polygone A
   * @param polyB Tableau de points {x, y} représentant les sommets du polygone B
   * @returns true si collision, false sinon
   */
  static isPolygonsColliding(polyA: Point2D[], polyB: Point2D[], tolerance: number = 1.5): boolean {
    const polygons = [polyA, polyB];
    let minA, maxA, projected, i, i1, j, minB, maxB;
    let minOverlap = Number.POSITIVE_INFINITY;

    for (i = 0; i < polygons.length; i++) {
      const polygon = polygons[i];
      for (i1 = 0; i1 < polygon.length; i1++) {
        const i2 = (i1 + 1) % polygon.length;
        const p1 = polygon[i1];
        const p2 = polygon[i2];

        // Trouver la normale (axe de projection)
        // Note: Normal doesn't need to be normalized for checking "gap", 
        // BUT for checking actual depth (tolerance), it MUST be normalized.
        let normal = { x: p2.y - p1.y, y: p1.x - p2.x };
        const len = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
        normal.x /= len;
        normal.y /= len;

        minA = maxA = undefined;
        // Projeter tous les points de A sur la normale
        for (j = 0; j < polyA.length; j++) {
          projected = normal.x * polyA[j].x + normal.y * polyA[j].y;
          if (minA == undefined || projected < minA) minA = projected;
          if (maxA == undefined || projected > maxA) maxA = projected;
        }

        minB = maxB = undefined;
        // Projeter tous les points de B sur la normale
        for (j = 0; j < polyB.length; j++) {
          projected = normal.x * polyB[j].x + normal.y * polyB[j].y;
          if (minB == undefined || projected < minB) minB = projected;
          if (maxB == undefined || projected > maxB) maxB = projected;
        }

        // Vérifier s'il y a un écart (gap)
        if (maxA < minB || maxB < minA) {
          return false; // Pas de collision
        }

        // Calculate overlap on this axis
        const overlap = Math.min(maxA, maxB) - Math.max(minA, minB);
        if (overlap < minOverlap) {
          minOverlap = overlap;
        }
      }
    }

    // Check if the minimal overlap is significant enough
    // If overlap is tiny (just touching), returns false.
    return minOverlap > tolerance;
  }

  /**
   * Retourne les sommets d'un rectangle tourné.
   * @param x Centre X
   * @param y Centre Y
   * @param width Largeur (avant rotation)
   * @param length Longueur/Hauteur (avant rotation)
   * @param angle Angle en radians
   */
  static getRotatedRectanglePoints(x: number, y: number, width: number, length: number, angle: number): Point2D[] {
    // Moitiés pour le calcul depuis le centre
    const halfW = width / 2;
    const halfL = length / 2;

    // Coins locaux (avant rotation), relatifs au centre (0,0)
    // Attention : l'orientation dépend de votre repère. Ici on suppose Width = X, Length = Y en local ?
    // Ou l'inverse selon votre convention Three.js.
    // Convention Three.js/Casto : Length = X (généralement), Width = Y.
    const corners = [
      { x: -halfL, y: -halfW },
      { x: halfL, y: -halfW },
      { x: halfL, y: halfW },
      { x: -halfL, y: halfW }
    ];

    return corners.map(p => {
      // Rotation
      const rotatedX = p.x * Math.cos(angle) - p.y * Math.sin(angle);
      const rotatedY = p.x * Math.sin(angle) + p.y * Math.cos(angle);
      // Translation
      return {
        x: rotatedX + x,
        y: rotatedY + y
      };
    });
  }
}
